import React, { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "react-toastify";

import {
  acceptMadeToMeasureQuote,
  getMadeToMeasureQuote,
  resetMadeToMeasureAcceptQuote,
  resetMadeToMeasurePublicQuote,
} from "../../actions/madeToMeasureActions";
import { getPaymentConfig } from "../../actions/paymentActions";
import Meta from "../../components/Meta";
import {
  selectMadeToMeasureAcceptQuote,
  selectMadeToMeasurePublicQuote,
} from "../../selectors/madeToMeasureSelectors";
import { selectPaymentConfig } from "../../selectors/paymentSelectors";
import { getSafeUserMessage } from "../../utils/errorMessages";

import "./MadeToMeasureScreen.css";

const FALLBACK_IMAGE = "https://placehold.co/640x760?text=Academy+Costumes+London";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function getImage(item = {}) {
  return safeText(item.image || item.defaultImage || item.images?.[0]?.url);
}

function SummaryRow({ item, label, price = "" }) {
  if (!item) return null;

  return (
    <div className="mtm-summary__item">
      <img
        src={getImage(item) || FALLBACK_IMAGE}
        alt={item.name || label}
        onError={(event) => {
          event.currentTarget.src = FALLBACK_IMAGE;
        }}
      />
      <div>
        <span>{label}</span>
        <strong>{item.name}</strong>
      </div>
      {price ? <em>{price}</em> : null}
    </div>
  );
}

function paymentUrl(payload = {}) {
  return (
    payload.checkoutUrl ||
    payload.approvalUrl ||
    payload.approveUrl ||
    payload.payerActionUrl ||
    payload.nextAction?.url ||
    ""
  );
}

export default function MadeToMeasureQuoteScreen() {
  const { token = "" } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const quoteState = useSelector(selectMadeToMeasurePublicQuote);
  const acceptState = useSelector(selectMadeToMeasureAcceptQuote);
  const paymentConfig = useSelector(selectPaymentConfig);

  const quote = quoteState.quote;
  const paymentReturn = safeText(searchParams.get("payment"));
  const stripeEnabled = Boolean(paymentConfig.stripeEnabled);
  const paypalEnabled = Boolean(paymentConfig.paypalEnabled);
  const sumupEnabled = Boolean(paymentConfig.sumupEnabled);
  const canPay = quote?.status !== "paid" && quote?.status !== "completed";

  useEffect(() => {
    dispatch(getMadeToMeasureQuote(token)).catch((error) => {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load this quote right now. Please try again later.",
        ),
      );
    });

    dispatch(getPaymentConfig()).catch(() => {});

    return () => {
      dispatch(resetMadeToMeasurePublicQuote());
      dispatch(resetMadeToMeasureAcceptQuote());
    };
  }, [dispatch, token]);

  const handleAccept = async (provider) => {
    try {
      const result = await dispatch(
        acceptMadeToMeasureQuote(token, { provider }),
      );
      const url = paymentUrl(result);

      if (url) {
        window.location.assign(url);
        return;
      }

      toast.info("Quote accepted. We could not open the payment page.");
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not accept this quote right now. Please try again later.",
        ),
      );
    }
  };

  return (
    <main className="mtm-page">
      <Meta
        title="Made To Measure Quote | Academy Costumes London"
        description="Review and accept your made to measure quote."
      />

      <header className="mtm-hero">
        <p className="mtm-eyebrow">Final quote</p>
        <h1>Made To Measure Quote</h1>
        <p>
          Review your final quote, confirm you are happy to proceed, then
          continue to secure payment.
        </p>
      </header>

      {quoteState.loading ? (
        <section className="mtm-state">
          <Loader2 className="mtm-spin" />
          <p>Loading your quote...</p>
        </section>
      ) : quoteState.error ? (
        <section className="mtm-state">
          <p>{quoteState.error}</p>
          <Link to="/contact" className="mtm-nav-button mtm-nav-button--primary">
            Contact us
          </Link>
        </section>
      ) : quote ? (
        <section className="mtm-quote">
          <div className="mtm-step-panel">
            {paymentReturn === "success" ? (
              <div className="mtm-success">
                <CheckCircle2 size={24} />
                <div>
                  <strong>Thank you.</strong>
                  <p>
                    If your payment has completed, your made to measure order
                    will be reviewed and confirmed by our team.
                  </p>
                </div>
              </div>
            ) : null}

            {paymentReturn === "cancelled" ? (
              <div className="mtm-warning mtm-warning--full">
                <span aria-hidden="true">!</span>
                <span>Payment was cancelled. You can still accept this quote.</span>
              </div>
            ) : null}

            <div className="mtm-step-panel__head">
              <div>
                <h2>Your final quote</h2>
                <p>
                  Prepared for {quote.customerName}. Final price is confirmed
                  before production begins.
                </p>
              </div>
            </div>

            <div className="mtm-summary mtm-summary--quote">
              <SummaryRow
                item={quote.selectedProduct}
                label="Design"
                price={formatMoney(quote.estimatedBasePrice)}
              />
              <SummaryRow
                item={quote.selectedTextile}
                label="Fabric"
                price=""
              />

              <div className="mtm-summary__group">
                <h3>Selected trims</h3>
                {quote.selectedHaberdashery?.length ? (
                  quote.selectedHaberdashery.map((item) => (
                    <SummaryRow key={item.id || item._id} item={item} label="Trim" />
                  ))
                ) : (
                  <p>No trims selected.</p>
                )}
              </div>

              {quote.quoteMessage ? (
                <div className="mtm-summary__group">
                  <h3>Quote notes</h3>
                  <p>{quote.quoteMessage}</p>
                </div>
              ) : null}

              <div className="mtm-summary__estimate">
                <div>
                  <span>Estimated base</span>
                  <strong>{formatMoney(quote.estimatedBasePrice)}</strong>
                </div>
                <div>
                  <span>Estimated extra</span>
                  <strong>{formatMoney(quote.estimatedExtraCost)}</strong>
                </div>
                <div>
                  <span>Final quote</span>
                  <strong>{formatMoney(quote.finalQuotePrice)}</strong>
                </div>
              </div>
            </div>

            {quote.status === "paid" ? (
              <div className="mtm-success mtm-success--stacked">
                <CheckCircle2 size={24} />
                <div>
                  <strong>Payment received.</strong>
                  <p>We will contact you before production begins.</p>
                </div>
              </div>
            ) : null}

            {canPay ? (
              <div className="mtm-submit-row">
                <p>
                  Accepting the quote will take you to secure payment. Production
                  starts only after payment and final confirmation.
                </p>

                <div className="mtm-payment-actions">
                  <button
                    type="button"
                    className="mtm-nav-button mtm-nav-button--primary"
                    disabled={!stripeEnabled || acceptState.loading}
                    onClick={() => handleAccept("stripe")}
                  >
                    {acceptState.loading ? (
                      <Loader2 size={16} className="mtm-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                    Pay by card
                  </button>

                  <button
                    type="button"
                    className="mtm-nav-button"
                    disabled={!paypalEnabled || acceptState.loading}
                    onClick={() => handleAccept("paypal")}
                  >
                    PayPal
                  </button>

                  {sumupEnabled ? (
                    <button
                      type="button"
                      className="mtm-nav-button"
                      disabled={acceptState.loading}
                      onClick={() => handleAccept("sumup")}
                    >
                      SumUp
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {!stripeEnabled && !paypalEnabled && !sumupEnabled && canPay ? (
              <p className="mtm-error">
                Online payment is not configured yet. Please contact us to
                accept this quote.
              </p>
            ) : null}

            {acceptState.error ? (
              <p className="mtm-error">{acceptState.error}</p>
            ) : null}
          </div>
        </section>
      ) : null}
    </main>
  );
}
